import React, { useState } from 'react';
import { Mail, Loader2, MailCheck, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '../../services/api';

// Asks for an email and starts a password reset. The confirmation is
// deliberately the same whether or not the address has an account, so this
// screen can't be used to find out who is registered.
const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setEmail('');
    setLoading(false);
    setSent(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'We could not start the reset just now. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {sent ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center">
                Check your email
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                <MailCheck className="h-7 w-7 text-emerald-600" aria-hidden="true" />
              </div>
              <p className="text-gray-600">
                If that email address has an account with us, we have sent it a link
                to reset the password.
              </p>
              <p className="text-sm text-gray-500">
                The link expires in 2 hours and can be used once. Do check your spam
                folder if it does not arrive shortly.
              </p>
              <Button
                onClick={() => {
                  reset();
                  onBackToLogin();
                }}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                Back to sign in
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center">
                Reset your password
              </DialogTitle>
              <p className="text-center text-gray-600">
                Enter the email address on your account and we will send you a link
                to set a new password.
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    required
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending link...
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>
            </form>

            <button
              type="button"
              onClick={() => {
                reset();
                onBackToLogin();
              }}
              className="flex items-center justify-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
